import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Trophy, Users, BookOpen } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import EmptyState from "@/components/EmptyState";
import ModeToggle from "@/components/shared/ModeToggle";
import { cn } from "@/lib/utils";
import { formatMoney, formatNumber } from "@/lib/format";

/**
 * The class ranked by minutes read (default — it's what students do
 * every day) or money raised. Rank numbers follow DESIGN.md: Fraunces,
 * top three in clay.
 */
export default function ClassroomLeaderboardCard({ students, log_minutes_path }) {
  const [mode, setMode] = useState("minutes");
  const isMoney = mode === "money";
  const valueOf = (s) => (isMoney ? s.raised_cents : s.minutes_logged);

  const sorted = [...students].sort((a, b) =>
    isMoney
      ? (b.raised_cents - a.raised_cents) || (b.minutes_logged - a.minutes_logged)
      : (b.minutes_logged - a.minutes_logged) || (b.raised_cents - a.raised_cents)
  );
  const topValue = sorted[0] ? valueOf(sorted[0]) : 0;

  return (
    <Card className="min-w-0">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Leaderboard
            </CardTitle>
            <CardDescription>Ranked by {isMoney ? "money raised" : "minutes read"}.</CardDescription>
          </div>
          <ModeToggle isMoney={isMoney} onMoney={() => setMode("money")} onMinutes={() => setMode("minutes")} />
        </div>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <EmptyState
            bare
            size="sm"
            icon={Users}
            title="No students yet"
            description="Your school admin adds the class list. Ask them to import your roster."
          />
        ) : topValue === 0 ? (
          <EmptyState
            bare
            size="sm"
            icon={BookOpen}
            title={isMoney ? "No donations yet" : "No minutes logged yet"}
            description={isMoney
              ? "Donations show up here as families share their pages."
              : "The leaderboard fills in as soon as the class's first reading is logged."}
            action={!isMoney && log_minutes_path && (
              <Link href={log_minutes_path} className="text-sm font-medium text-primary hover:underline">Log the first session</Link>
            )}
          />
        ) : (
          <ol className="space-y-2">
            {sorted.map((student, i) => (
              <LeaderboardRow
                key={student.id}
                rank={i + 1}
                student={student}
                isMoney={isMoney}
                barPct={Math.round((valueOf(student) / topValue) * 100)}
                ranked={valueOf(student) > 0}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function LeaderboardRow({ rank, student, isMoney, barPct, ranked }) {
  const primary = isMoney ? formatMoney(student.raised_cents) : `${formatNumber(student.minutes_logged)} min`;
  const secondary = isMoney ? `${formatNumber(student.minutes_logged)} min` : formatMoney(student.raised_cents);

  return (
    <li className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 sm:gap-4 sm:px-4">
      <RankNumber rank={rank} ranked={ranked} />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          {student.student_path ? (
            <Link href={student.student_path} className="min-w-0 break-words font-medium text-foreground transition-colors hover:text-primary">
              {student.full_name}
            </Link>
          ) : (
            <p className="min-w-0 break-words font-medium text-foreground">{student.full_name}</p>
          )}
          <div className="flex shrink-0 items-baseline gap-3 font-tabular">
            <span className="hidden text-sm text-muted-foreground sm:inline">{secondary}</span>
            <span className="font-semibold text-primary">{primary}</span>
          </div>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${barPct}%` }} />
        </div>
      </div>
    </li>
  );
}

// Only students with something logged get a podium colour.
function RankNumber({ rank, ranked }) {
  const podium = ranked && rank <= 3;
  return (
    <span
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-lg font-semibold font-tabular",
        podium ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"
      )}
      aria-label={`Rank ${rank}`}
    >
      {rank}
    </span>
  );
}
